import type { ChangeEventHandler, FocusEvent, FormEventHandler, MouseEventHandler } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { Checkbox } from 'src/shared/components/checkbox/checkbox.js';
import { Form } from 'src/shared/components/form/form.js';
import { Input } from 'src/shared/components/input/input.js';
import { PasswordInput } from 'src/shared/components/password-input/password-input.js';
import { useFieldMessage } from 'src/shared/hooks/use-field-message.js';
import { useFormValidator } from 'src/shared/hooks/use-form-validator.js';
import { usePasswordStrengthCriteria } from 'src/shared/hooks/use-password-strength-criteria.js';
import { useSavedFieldLs } from 'src/shared/hooks/use-saved-field-ls.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';
import { getProfileAttr } from 'src/shared/utils/profile/get-profile-attr.js';

import { cnRegister } from './register.constants.js';

import './register.css';

export interface RegisterProps {
    kcContext: KcContext.Register;
}

export const Register = memo<RegisterProps>(({ kcContext }) => {
    const { url, passwordRequired } = kcContext;

    const [onChangeSavedField, getSavedField] = useSavedFieldLs();

    const { msgStr, advancedMsgStr } = useGetMsg(kcContext);

    const emailError = useFieldMessage(['email', 'username'], kcContext);
    const passwordError = useFieldMessage(['password', 'password-confirm'], kcContext);

    // redirect to login page if user didn't come from login page
    // i.e. user opened registration page directly
    useEffect(() => {
        const email = getSavedField('email');
        if (!email) {
            window.location.href = url.loginUrl;
        }
    }, [getSavedField, url.loginUrl]);

    const saveEmail = useCallback((formElement: HTMLFormElement) => {
        if (formElement['email']) {
            const currentEmail: string = (formElement['email'] as HTMLInputElement).value ?? '';
            sessionStorage.setItem('userEmail', currentEmail);
        }

        return true;
    }, []);

    const [formErrors, resetFieldError, onSubmitValidated, submitDisabled, setFormErrors] = useFormValidator(
        ['firstName', 'email', 'password', 'privacyPolicy'],
        { email: emailError, password: passwordError },
        saveEmail,
    );

    const handleSubmition: React.FormEventHandler<HTMLFormElement> = useCallback(
        (e) => {
            onSubmitValidated(e);
        },
        [onSubmitValidated],
    );

    const [password, setPassword] = useState('');

    const handlePasswordChange = useCallback<FormEventHandler<HTMLInputElement>>((e) => {
        if (!e.target) {
            return;
        }

        setPassword((e?.target as HTMLInputElement).value);
    }, []);

    const formRef = useRef<HTMLFormElement | null>(null);

    const onSocialClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
        (e) => {
            if (!formRef.current) {
                return;
            }

            if (formRef.current['privacyPolicy'].checked) {
                return;
            }

            e.preventDefault();

            setFormErrors({
                ...formErrors,
                privacyPolicy: 'formErrorAcceptPrivacyPolicy',
            });
        },
        [formErrors, setFormErrors],
    );

    const inputFocusHandler = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            resetFieldError(e);
        },
        [resetFieldError],
    );

    const passwordStrengthLevels = usePasswordStrengthCriteria(msgStr);

    const redirectToLogin = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (e) => {
            onChangeSavedField(e);
            window.location.href = url.loginUrl;
        },
        [onChangeSavedField, url.loginUrl],
    );

    return (
        <Form
            action={url.registrationAction}
            kcContext={kcContext}
            onSubmit={handleSubmition}
            onSocialClick={onSocialClick}
            ref={formRef}
        >
            <Input
                type="text"
                id="email"
                name="email"
                defaultValue={getProfileAttr(kcContext, 'email') || getSavedField('email')}
                label={msgStr('email')}
                autoComplete="email"
                errorMessage={advancedMsgStr(formErrors['email'] ?? '')}
                onFocus={inputFocusHandler}
                onChange={redirectToLogin}
                autoFocus
            />

            <div className={cnRegister('tip-after-redirect')}>{msgStr('noAccount')}</div>

            <Input
                type="text"
                id="firstName"
                name="firstName"
                defaultValue={getProfileAttr(kcContext, 'firstName') || getSavedField('firstName')}
                label={msgStr('firstName')}
                errorMessage={advancedMsgStr(formErrors['firstName'] ?? '')}
                onFocus={inputFocusHandler}
                onChange={onChangeSavedField}
            />

            {passwordRequired && (
                <PasswordInput
                    className={cnRegister('password-strength')}
                    errorMessage={advancedMsgStr(formErrors['password'] ?? '')}
                    hintText={msgStr('passwordStrengthHint')}
                    label={msgStr('password')}
                    onChange={handlePasswordChange}
                    onFocus={inputFocusHandler}
                    password={password}
                    strengthLevels={passwordStrengthLevels}
                    value={password}
                />
            )}

            <Checkbox
                type="checkbox"
                id="privacyPolicy"
                name="privacyPolicy"
                onChange={onChangeSavedField}
                errorMessage={advancedMsgStr(formErrors['privacyPolicy'] ?? '')}
                onClick={resetFieldError}
                className={cnRegister('privacy-policy-label')}
                childrenWrapperClassName={cnRegister('privacy-policy-label-children-wrapper')}
            >
                {msgStr('accept')}&nbsp;
                <a href={msgStr('tosPageLink')} target="blank" className={cnRegister('privacy-policy-link')}>
                    {msgStr('privacyPolicy')}
                </a>
            </Checkbox>

            <Input type="submit" value={msgStr('doRegister')} disabled={submitDisabled} />
        </Form>
    );
});
